import React, { useEffect } from "react";
import {
  Accordion,
  Badge,
  Box,
  Button,
  Center,
  Checkbox,
  Group,
  Loader,
  Stack,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";

import { useGetUserPermissions } from "api/permission/getpermission";
import { useUpdateUserPermission } from "api/permission/getAllUsersAndPermissions";
import { useResetUserPermissions } from "api/permission/resetUserPermissions";
import { useHasPermission } from "api/permission/useHasPermission";
import { useUser } from "hooks/useUser";
import { canManageRole, UserRole } from "utils/roleHierarchy";
import { getPermissionCategoryLabel, getPermissionLabel } from "utils/permissionLabels";
import { PERMISSION_CATEGORY_ORDER, sortSubPermissions } from "utils/permissionSorter";
import {
  mergeWithPermissionSkeleton,
  PERMISSIONS_SKELETON,
  PermissionsShape,
} from "utils/permissionSchema";

type Props = {
  memberId: string;
};

const CATEGORY_KEYS = Object.keys(PERMISSIONS_SKELETON) as Array<keyof PermissionsShape>;
const FLAT_CATEGORIES = CATEGORY_KEYS.filter(
  (key) => typeof PERMISSIONS_SKELETON[key] === "boolean"
);
const NESTED_CATEGORIES_UNORDERED = CATEGORY_KEYS.filter(
  (key) => typeof PERMISSIONS_SKELETON[key] === "object"
);
const NESTED_CATEGORIES = [
  ...PERMISSION_CATEGORY_ORDER.filter((c) =>
    NESTED_CATEGORIES_UNORDERED.includes(c as keyof PermissionsShape)
  ),
  ...NESTED_CATEGORIES_UNORDERED.filter((c) => !PERMISSION_CATEGORY_ORDER.includes(c)),
] as Array<keyof PermissionsShape>;

export function PermissionAccordionEditor({ memberId }: Props) {
  const { user } = useUser();
  const { data, isLoading, isError } = useGetUserPermissions(memberId);
  const updateMutation = useUpdateUserPermission();
  const resetMutation = useResetUserPermissions();
  const { has: canEdit } = useHasPermission("permission_management.edit_users_permissions");

  const targetRole = data?.data?.role;
  const canManageTarget = canManageRole(user?.role as UserRole, targetRole as UserRole);
  const editingAllowed = canEdit && canManageTarget;

  const form = useForm<PermissionsShape>({
    initialValues: PERMISSIONS_SKELETON,
  });

  useEffect(() => {
    if (data?.data?.permissions) {
      const merged = mergeWithPermissionSkeleton(data.data.permissions);
      form.setValues(merged);
      form.resetDirty(merged);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (isLoading) {
    return (
      <Center py="md">
        <Loader size="sm" />
      </Center>
    );
  }

  if (isError) {
    return <Text color="red">Failed to load permissions</Text>;
  }

  const handleSave = () => {
    updateMutation.mutate(
      { id: memberId, data: { permissions: form.values } },
      { onSuccess: () => form.resetDirty() }
    );
  };

  const handleReset = () => {
    resetMutation.mutate(memberId, {
      onSuccess: (res) => {
        const merged = mergeWithPermissionSkeleton(res.data.permissions);
        form.setValues(merged);
        form.resetDirty(merged);
      },
    });
  };

  return (
    <Stack spacing="sm">
      {!editingAllowed && (
        <Text size="xs" color="dimmed">
          {!canEdit
            ? "You don't have permission to edit permissions."
            : "You cannot manage a user with this role."}
        </Text>
      )}

      <Box>
        <Text size="sm" fw={600} mb={4}>
          General Access
        </Text>
        <Group spacing="md">
          {FLAT_CATEGORIES.map((category) => (
            <Checkbox
              key={category}
              label={getPermissionCategoryLabel(category)}
              disabled={!editingAllowed}
              {...form.getInputProps(category, { type: "checkbox" })}
            />
          ))}
        </Group>
      </Box>

      <Accordion variant="contained" multiple>
        {NESTED_CATEGORIES.map((category) => {
          const skeletonSubKeys = PERMISSIONS_SKELETON[category] as Record<string, boolean>;
          const sortedSubKeys = Object.keys(sortSubPermissions(category, skeletonSubKeys));
          const values = (form.values[category] as Record<string, boolean>) ?? {};
          const enabledCount = sortedSubKeys.filter((key) => values[key]).length;
          const allChecked = enabledCount === sortedSubKeys.length;
          const someChecked = enabledCount > 0 && !allChecked;

          const toggleAll = (checked: boolean) => {
            sortedSubKeys.forEach((key) => {
              form.setFieldValue(`${category}.${key}`, checked);
            });
          };

          return (
            <Accordion.Item key={category} value={category}>
              <Accordion.Control>
                <Group position="apart" pr="sm">
                  <div
                    role="presentation"
                    onClick={(event) => event.stopPropagation()}
                    onMouseDown={(event) => event.stopPropagation()}
                  >
                    <Checkbox
                      label={getPermissionCategoryLabel(category)}
                      checked={allChecked}
                      indeterminate={someChecked}
                      disabled={!editingAllowed}
                      onChange={(event) => toggleAll(event.currentTarget.checked)}
                    />
                  </div>
                  <Badge size="sm" variant="light" color={enabledCount > 0 ? "teal" : "gray"}>
                    {enabledCount}/{sortedSubKeys.length}
                  </Badge>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack spacing={6}>
                  {sortedSubKeys.map((subKey) => (
                    <Checkbox
                      key={subKey}
                      label={getPermissionLabel(`${category}.${subKey}`)}
                      disabled={!editingAllowed}
                      {...form.getInputProps(`${category}.${subKey}`, { type: "checkbox" })}
                    />
                  ))}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          );
        })}
      </Accordion>

      {editingAllowed && (
        <Group position="right" mt="sm">
          <Button
            variant="subtle"
            color="gray"
            size="xs"
            onClick={handleReset}
            loading={resetMutation.isLoading}
          >
            Reset to Role Defaults
          </Button>
          <Button
            size="xs"
            onClick={handleSave}
            disabled={!form.isDirty()}
            loading={updateMutation.isLoading}
          >
            Save Permissions
          </Button>
        </Group>
      )}
    </Stack>
  );
}
